import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Rozet } from "@kantin/ui";
import { HareketTipi, para, paraGoster } from "@kantin/shared";
import { apiYaz } from "./api.js";
import { IslemDurumu, MetinAlani, TehlikeliOnay, paraGecerliMi, paraNormalize, tarihSaatGoster } from "./ortak.js";

export interface GeriAlinabilirYukleme {
  id: string;
  kisi_ad: string;
  tutar: string;
  yukleme_tipi: number | null;
  geri_alma_id: string | null;
  geri_alma_aciklamasi: string | null;
  geri_alma_zamani: string | null;
  geri_alinan_tutar: string;
  kalan_tutar: string;
}

export function YuklemeGeriAl({ kayit }: { kayit: GeriAlinabilirYukleme }) {
  const istemci = useQueryClient();
  const [acik, setAcik] = useState(false);
  const [gerekce, setGerekce] = useState("");
  const [gerekceHatasi, setGerekceHatasi] = useState(false);
  const [tutar, setTutar] = useState("");
  const [tutarHatasi, setTutarHatasi] = useState<string>();
  const islem = useMutation({
    mutationFn: (govde: { tutar: string; gerekce: string; son_geri_alma_id: string | null }) => apiYaz<{
      yeni_bakiye: string; geri_alma_id: string; geri_alinan_tutar: string; kalan_tutar: string; uyari?: string;
    }>(
      "POST", `/yukleme/bildirim/${kayit.id}/geri-al`, govde,
    ),
    onSuccess: () => {
      setAcik(false);
      // Öğrenci bakiyesi, yükleme geçmişi, mutabakat ve gösterge birlikte yenilenir.
      void istemci.invalidateQueries();
    },
  });
  // Liste yenilenene kadar başarılı yanıtı kullan; daha yeni listeyi ezme.
  const sonuc = islem.data && para(islem.data.geri_alinan_tutar).greaterThan(kayit.geri_alinan_tutar)
    ? islem.data : kayit;
  const tamamenGeriAlindi = para(sonuc.kalan_tutar).isZero();

  return (
    <div className="w-full">
      {para(sonuc.geri_alinan_tutar).greaterThan(0) && <div className="mb-2 text-sm">
        <Rozet tur="uyari">{tamamenGeriAlindi ? "Geri alındı" : "Kısmen geri alındı"}</Rozet>
        <p>Geri alınan: {paraGoster(sonuc.geri_alinan_tutar)} · Kalan: {paraGoster(sonuc.kalan_tutar)}</p>
        {kayit.geri_alma_zamani && <span className="ml-2">{tarihSaatGoster(kayit.geri_alma_zamani)}</span>}
        {kayit.geri_alma_aciklamasi && <p className="mb-0">{kayit.geri_alma_aciklamasi}</p>}
      </div>}
      {islem.data && <p role="status">Yeni bakiye: {paraGoster(islem.data.yeni_bakiye)}</p>}
      {islem.data?.uyari && <p role="status" className="text-sm">{islem.data.uyari}</p>}
      {!tamamenGeriAlindi && (!acik ? (
        <Buton tur="tehlike" onClick={() => {
          setTutar(sonuc.kalan_tutar);
          setGerekce("");
          setGerekceHatasi(false);
          setTutarHatasi(undefined);
          setAcik(true);
        }}>Yüklemeyi geri al</Buton>
      ) : (
        <TehlikeliOnay
          soru={`${kayit.kisi_ad} için geri alınacak tutarı belirleyin`}
          aciklama={kayit.yukleme_tipi === HareketTipi.YUKLEME_NAKIT
            ? "Tutar öğrencinin bakiyesinden düşer; tahsilat noktasında açık vardiya varsa onun beklenen nakdinden de düşer, yoksa gün sonu sayımına girmez. Elden para iadesi gerekiyorsa okul tarafından yapılmalıdır. Bakiye yetersizse işlem engellenir."
            : "Tutar öğrencinin bakiyesinden düşer. Bu işlem bankadan para iadesi yapmaz; gerekiyorsa okul ayrıca gerçekleştirir. Bakiye yetersizse işlem engellenir."}
          onayMetni="Belirtilen tutarı geri al"
          onayla={() => {
            if (!paraGecerliMi(tutar) || !para(paraNormalize(tutar)).greaterThan(0)) {
              setTutarHatasi("Sıfırdan büyük bir tutarı 50 veya 50,00 biçiminde girin.");
              return;
            }
            if (para(paraNormalize(tutar)).greaterThan(sonuc.kalan_tutar)) {
              setTutarHatasi("Tutar, yüklemenin kalan tutarını aşamaz.");
              return;
            }
            setTutarHatasi(undefined);
            if (gerekce.trim().length < 5 || gerekce.trim().length > 500) {
              setGerekceHatasi(true);
              return;
            }
            setGerekceHatasi(false);
            islem.mutate({ tutar: paraNormalize(tutar), gerekce: gerekce.trim(), son_geri_alma_id: sonuc.geri_alma_id });
          }}
          vazgec={() => {
            setAcik(false);
            if (islem.isError) islem.reset();
          }}
          yukleniyor={islem.isPending}
          cocuk={<div className="flex flex-col gap-3">
            <Alan
              etiket="Geri alınacak tutar (TL)"
              value={tutar}
              onChange={(e) => setTutar(e.target.value)}
              inputMode="decimal"
              disabled={islem.isPending}
              ipucu={`En fazla ${paraGoster(sonuc.kalan_tutar)} geri alabilirsiniz.`}
              hata={tutarHatasi}
            />
            <MetinAlani
              etiket="Geri alma gerekçesi"
              value={gerekce}
              onChange={(e) => setGerekce(e.target.value)}
              maxLength={500}
              disabled={islem.isPending}
              hata={gerekceHatasi ? "En az 5 karakter gerekçe yazın." : undefined}
            />
          </div>}
        />
      ))}
      <IslemDurumu hata={islem.error} />
    </div>
  );
}
